// React and related imports
import { useState, useCallback } from 'react';

// Types and Interfaces
import { ContextState } from './useContentContextState';

// Redux selectors, hooks, and actions
import { useAppDispatch } from '../redux/hooks/useAppDispatch';
import { clearValidationErrors } from '../redux/slices/contentContextSlice';

interface UsePrayerTimeSettingsProps {
  initialMode: 'time' | 'prayer';
  contextState: ContextState;
  validateField: (field: string, value: string) => void;
  updateField: (field: keyof ContextState, value: string) => void;
}

export const usePrayerTimeSettings = ({
  initialMode,
  contextState,
  validateField,
  updateField,
}: UsePrayerTimeSettingsProps) => {
  const dispatch = useAppDispatch();
  const [selectionMode, setSelectionMode] = useState<'time' | 'prayer'>(
    initialMode
  );

  const handleValidation = useCallback(
    (field: string, value: string) => {
      if (!value || value === '--:--' || value === '-' || value === '+') {
        validateField(field, '');
        return false;
      }
      validateField(field, value);
      return true;
    },
    [validateField]
  );

  const handleFieldUpdate = useCallback(
    (field: keyof ContextState, value: string) => {
      updateField(field, value);

      if (field === 'time') {
        updateField('prayerName' as keyof ContextState, '');
        updateField('adhanDelta' as keyof ContextState, '');
      } else if (field === 'prayerName' || field === 'adhanDelta') {
        updateField('time' as keyof ContextState, '');
      }

      handleValidation(field, value);
    },
    [updateField, handleValidation]
  );

  const handleSelectionChange = useCallback(
    (mode: 'time' | 'prayer') => {
      setSelectionMode(mode);

      if (mode === 'time') {
        handleValidation('time', contextState?.time || '');
        dispatch(clearValidationErrors('prayerName'));
        dispatch(clearValidationErrors('adhanDelta'));
      } else {
        handleValidation('prayerName', contextState?.prayerName || '');
        handleValidation('adhanDelta', contextState?.adhanDelta || '');
        dispatch(clearValidationErrors('time'));
      }
    },
    [dispatch, contextState, handleValidation]
  );

  return {
    selectionMode,
    handleSelectionChange,
    handleFieldUpdate,
  };
};
